/*
  <static-pdp-gallery>
  Fully custom media gallery for the Static PDP template.
  - Prev/next arrows on the main image (desktop + mobile)
  - Thumbnail strip with prev/next paging (desktop) and native swipe (mobile)
  - Desktop shows 5 thumbnails at a time, mobile shows 4
  - Thumbnails auto-scroll into view and sync when the main prev/next
    controls (or a thumbnail click) change the active image
*/
if (!customElements.get('static-pdp-gallery')) {
  customElements.define(
    'static-pdp-gallery',
    class StaticPdpGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.spdp-gallery__track');
        this.slides = Array.from(this.querySelectorAll('.spdp-gallery__slide'));
        this.thumbs = Array.from(this.querySelectorAll('.spdp-gallery__thumb'));
        this.thumbsViewport = this.querySelector('.spdp-gallery__thumbs-viewport');
        this.prevBtn = this.querySelector('.spdp-gallery__arrow--prev');
        this.nextBtn = this.querySelector('.spdp-gallery__arrow--next');
        this.thumbPrevBtn = this.querySelector('.spdp-gallery__thumb-nav--prev');
        this.thumbNextBtn = this.querySelector('.spdp-gallery__thumb-nav--next');

        this.index = 0;
        this.total = this.slides.length;

        if (this.total <= 1) {
          if (this.prevBtn) this.prevBtn.hidden = true;
          if (this.nextBtn) this.nextBtn.hidden = true;
        }

        this.prevBtn?.addEventListener('click', () => this.go(this.index - 1));
        this.nextBtn?.addEventListener('click', () => this.go(this.index + 1));

        this.thumbPrevBtn?.addEventListener('click', () => this.pageThumbs(-1));
        this.thumbNextBtn?.addEventListener('click', () => this.pageThumbs(1));

        this.thumbs.forEach((thumb, i) => {
          const btn = thumb.querySelector('.spdp-gallery__thumb-btn');
          btn?.addEventListener('click', () => this.go(i));
        });

        this.bindSwipe();
        this.thumbsViewport?.addEventListener('scroll', () => this.updateThumbNavState(), {
          passive: true,
        });

        this.render(true);

        window.addEventListener('resize', () => this.render(false));
      }

      go(newIndex) {
        if (this.total === 0) return;
        this.index = Math.max(0, Math.min(newIndex, this.total - 1));
        this.render(true);
      }

      render(scrollThumbIntoView) {
        if (!this.track) return;
        this.track.style.transform = `translateX(-${this.index * 100}%)`;

        if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index === this.total - 1;

        this.thumbs.forEach((thumb, i) => {
          const isCurrent = i === this.index;
          thumb.setAttribute('aria-current', isCurrent ? 'true' : 'false');
        });

        if (scrollThumbIntoView) this.scrollThumbIntoView();
        this.updateThumbNavState();
      }

      scrollThumbIntoView() {
        const activeThumb = this.thumbs[this.index];
        if (!activeThumb || !this.thumbsViewport) return;

        const viewport = this.thumbsViewport;
        const viewportRect = viewport.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();

        const isVisible =
          thumbRect.left >= viewportRect.left && thumbRect.right <= viewportRect.right;

        if (!isVisible) {
          activeThumb.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
          });
        }
      }

      pageThumbs(direction) {
        if (!this.thumbsViewport) return;
        // Scroll by one full "page" (the visible thumbnail count for this breakpoint).
        const step = this.thumbsViewport.clientWidth * direction;
        this.thumbsViewport.scrollBy({ left: step, behavior: 'smooth' });
      }

      updateThumbNavState() {
        if (!this.thumbsViewport || !this.thumbPrevBtn || !this.thumbNextBtn) return;
        const { scrollLeft, scrollWidth, clientWidth } = this.thumbsViewport;
        this.thumbPrevBtn.disabled = scrollLeft <= 2;
        this.thumbNextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
      }

      bindSwipe() {
        const viewport = this.querySelector('.spdp-gallery__viewport');
        if (!viewport) return;

        let startX = 0;
        let currentX = 0;
        let dragging = false;

        const threshold = 40;

        const onStart = (x) => {
          dragging = true;
          startX = x;
          currentX = x;
          this.track.style.transition = 'none';
        };

        const onMove = (x) => {
          if (!dragging) return;
          currentX = x;
          const delta = currentX - startX;
          const percentDelta = (delta / viewport.clientWidth) * 100;
          this.track.style.transform = `translateX(calc(-${this.index * 100}% + ${percentDelta}%))`;
        };

        const onEnd = () => {
          if (!dragging) return;
          dragging = false;
          this.track.style.transition = '';
          const delta = currentX - startX;

          if (Math.abs(delta) > threshold) {
            if (delta < 0) this.go(this.index + 1);
            else this.go(this.index - 1);
          } else {
            this.render(false);
          }
        };

        viewport.addEventListener('touchstart', (e) => onStart(e.touches[0].clientX), {
          passive: true,
        });
        viewport.addEventListener('touchmove', (e) => onMove(e.touches[0].clientX), {
          passive: true,
        });
        viewport.addEventListener('touchend', onEnd);
      }
    }
  );
}
